import { useEffect, useMemo, useState } from 'react';
import useBoundingRectBySelector from './useBoundingRectBySelector';

interface UseTableScrollConfigParams {
  /**
   * Minimum width that triggers horizontal scrolling. Omit to never scroll —
   * AntD treats an absent `scroll.x` as "fit the container".
   */
  x?: number;
  rowCount: number;
  desktopOffsetEm: number;
  leadingHeight?: number;
  /** CSS selector for the element whose height drives scroll `y` (tabs chrome is subtracted). Omit or pass null to use viewport fallback only. */
  wrapperSelector?: string | null;
  /**
   * Bottom padding (px) of the detail-panel content area below the tabs — the
   * gap kept between the table's bottom and the viewport bottom when the table
   * overflows. Defaults to `EM_IN_PIXELS` (16) for the legacy `py-4` layout;
   * callers whose container uses a larger bottom padding (e.g. `pb-8` = 32)
   * pass that value so the measured height is capped to the correct slot.
   */
  containerBottomPaddingPx?: number;
}

const EM_IN_PIXELS = 16;
const MOBILE_HEIGHT_PX = 124;
const DESKTOP_BREAKPOINT_PX = 720;
const ESTIMATED_ROW_HEIGHT_PX = 60;
const OVERFLOW_TOLERANCE_PX = 1;
const HEIGHT_COMPENSATION_PX = 1;

const TABLE_HEAD_RESERVE_PX = 53;
const TABLE_FOOTER_RESERVE_PX = 56;
const TABLE_PADDING_TOP_PX = 24;
const TABLE_PADDING_BOTTOM_PX = 32;
const TABS_HEADER_HEIGHT_PX = 40;
const CONTAINER_BORDER_COMPENSATION_PX = 2;

/**
 * Total vertical space (px) subtracted from the measured wrapper height when deriving scrollable
 * body height: table head, footer, padding, tabs header, and container border compensation.
 */
const TABLE_CHROME_RESERVE_PX =
  TABLE_HEAD_RESERVE_PX +
  TABLE_FOOTER_RESERVE_PX +
  TABLE_PADDING_TOP_PX +
  TABLE_PADDING_BOTTOM_PX +
  TABS_HEADER_HEIGHT_PX +
  CONTAINER_BORDER_COMPENSATION_PX;

const useTableScrollConfig = ({
  x,
  rowCount,
  desktopOffsetEm,
  leadingHeight = 0,
  wrapperSelector,
  containerBottomPaddingPx = EM_IN_PIXELS
}: UseTableScrollConfigParams): { x?: number; y?: number } => {
  const hasWindow = globalThis.window !== undefined;
  const [windowHeight, setWindowHeight] = useState<number | null>(
    hasWindow ? globalThis.window.innerHeight : null
  );

  useEffect(() => {
    if (!hasWindow) {
      return () => {};
    }

    const handleResize = () => {
      setWindowHeight(globalThis.window.innerHeight);
    };

    globalThis.window.addEventListener('resize', handleResize);

    return () => {
      globalThis.window.removeEventListener('resize', handleResize);
    };
  }, [hasWindow]);

  const tableScrollY = useMemo(() => {
    if (windowHeight && windowHeight >= DESKTOP_BREAKPOINT_PX) {
      return Math.max(
        windowHeight - desktopOffsetEm * EM_IN_PIXELS + HEIGHT_COMPENSATION_PX,
        0
      );
    }

    return MOBILE_HEIGHT_PX + HEIGHT_COMPENSATION_PX;
  }, [windowHeight, desktopOffsetEm]);

  // Measured wrapper bounds for the table, when a selector is provided.
  const wrapperBoundingRect = useBoundingRectBySelector(wrapperSelector);

  // Target height (px) for the scrollable table body, from measurement when possible or a fallback.
  const bodyScrollHeightPx = useMemo(() => {
    const rect = wrapperBoundingRect;
    if (rect && typeof rect.height === 'number') {
      const { height: measuredHeight, top } = rect;

      // The measured wrapper grows with the table's scroll height (ResizeObserver feedback).
      // Cap height to the viewport slot below the element so scrollY does not run away.
      const maxHeightInViewport =
        typeof windowHeight === 'number' && windowHeight > 0
          ? Math.max(windowHeight - top - containerBottomPaddingPx, 0) // reserve the container's bottom padding below the table
          : measuredHeight;

      // Limit the wrapper height to the viewport slot below the element so scrollY does not run away.
      const effectiveWrapperHeight = Math.min(
        measuredHeight,
        maxHeightInViewport
      );

      // Subtract the combined vertical offset for non-body table layout around the scrollable area.
      return Math.max(
        effectiveWrapperHeight - TABLE_CHROME_RESERVE_PX - leadingHeight,
        0
      );
    }

    return tableScrollY;
  }, [
    wrapperBoundingRect,
    tableScrollY,
    leadingHeight,
    windowHeight,
    containerBottomPaddingPx
  ]);

  const shouldEnableVerticalScroll = useMemo(() => {
    const estimatedContentHeight = rowCount * ESTIMATED_ROW_HEIGHT_PX;
    return estimatedContentHeight > bodyScrollHeightPx + OVERFLOW_TOLERANCE_PX;
  }, [rowCount, bodyScrollHeightPx]);

  return useMemo(() => {
    if (shouldEnableVerticalScroll) {
      return {
        x,
        y: bodyScrollHeightPx
      };
    }

    return {
      x
    };
  }, [x, shouldEnableVerticalScroll, bodyScrollHeightPx]);
};

export default useTableScrollConfig;
